
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FormField } from "./BaseForm";
import { ProductLiabilityFormData, FormProps } from "@/types/forms";

type ProductLiabilityFormProps = FormProps<ProductLiabilityFormData>;

const ProductLiabilityForm = ({ onSubmit, initialData = {}, buttonText = "Continue" }: ProductLiabilityFormProps) => {
  const [formData, setFormData] = useState<ProductLiabilityFormData>({
    productType: initialData.productType || "",
    businessModel: initialData.businessModel || "",
    distributeOutsideUAE: initialData.distributeOutsideUAE || "",
    annualTurnover: initialData.annualTurnover || "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.productType) newErrors.productType = "Product type is required";
    if (!formData.businessModel) newErrors.businessModel = "Business model is required";
    if (!formData.distributeOutsideUAE) newErrors.distributeOutsideUAE = "Please specify distribution scope";
    if (!formData.annualTurnover) newErrors.annualTurnover = "Annual turnover is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const updateField = <K extends keyof ProductLiabilityFormData>(field: K, value: ProductLiabilityFormData[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <FormField label="Type of products sold" required>
        <Input
          placeholder="Enter type of products you sell"
          value={formData.productType}
          onChange={(e) => updateField('productType', e.target.value)}
        />
        {errors.productType && <p className="text-red-500 text-sm">{errors.productType}</p>}
      </FormField>

      <FormField label="Do you manufacture, import, or just sell these products?" required>
        <Select value={formData.businessModel} onValueChange={(value) => updateField('businessModel', value)}>
          <SelectTrigger>
            <SelectValue placeholder="Select option" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="manufacture">Manufacture</SelectItem>
            <SelectItem value="import">Import</SelectItem>
            <SelectItem value="sell">Just sell</SelectItem>
          </SelectContent>
        </Select>
        {errors.businessModel && <p className="text-red-500 text-sm">{errors.businessModel}</p>}
      </FormField>

      <FormField label="Do you distribute outside the UAE?" required>
        <Select value={formData.distributeOutsideUAE} onValueChange={(value) => updateField('distributeOutsideUAE', value)}>
          <SelectTrigger>
            <SelectValue placeholder="Select option" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="yes">Yes</SelectItem>
            <SelectItem value="no">No</SelectItem>
          </SelectContent>
        </Select>
        {errors.distributeOutsideUAE && <p className="text-red-500 text-sm">{errors.distributeOutsideUAE}</p>}
      </FormField>

      <FormField label="Estimated annual turnover (AED)" required>
        <Input
          type="number"
          placeholder="Enter estimated annual turnover in AED"
          value={formData.annualTurnover}
          onChange={(e) => updateField('annualTurnover', e.target.value)}
        />
        {errors.annualTurnover && <p className="text-red-500 text-sm">{errors.annualTurnover}</p>}
      </FormField>

      <Button type="submit" className="w-full">
        {buttonText}
      </Button>
    </form>
  );
};

export default ProductLiabilityForm;
